import React, { useRef, useEffect, useState, useCallback } from 'react';

/**
 * VideoReader Component with Sound Range Controls
 * 
 * Features:
 * - Prevents seeking backward in video
 * - Custom volume controls with configurable range
 * - Mute/unmute functionality
 * - Volume slider with visual feedback
 * - Skip button for development
 * 
 * Usage:
 * <VideoReader
 *   url="video.mp4"
 *   onFinish={() => console.log('Video finished')}
 *   minVolume={0.1}
 *   maxVolume={0.9}
 *   initialVolume={0.5}
 *   showVolumeControls={true}
 * />
 */

interface VideoReaderProps {
  url: string;
  onFinish: () => void;
  className?: string;
  width?: string | number;
  height?: string | number;
  startButtonText?: string;
  overlayText?: string;
  showSkipButton?: boolean;
  minVolume?: number;
  maxVolume?: number;
  initialVolume?: number;
  showVolumeControls?: boolean;
}

export const VideoReader: React.FC<VideoReaderProps> = ({
  url,
  onFinish,
  className = '',
  width = '100%',
  height = 'auto',
  startButtonText = 'Commencer la vidéo',
  overlayText = 'Cliquez sur le bouton pour commencer la vidéo. ',
  showSkipButton = process.env.NODE_ENV === 'development',
  minVolume = 0,
  maxVolume = 1,
  initialVolume = 1,
  showVolumeControls = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [lastValidTime, setLastValidTime] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentVolume, setCurrentVolume] = useState(initialVolume);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(initialVolume);

  // Handle time updates to track current position
  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      setCurrentTime(currentTime);
      
      // Only allow forward progress, not backward
      if (currentTime >= lastValidTime) {
        setLastValidTime(currentTime);
      } else {
        // If user tried to skip backward, return to last valid position
        videoRef.current.currentTime = lastValidTime;
      }
    }
  }, [lastValidTime]);

  // Handle seeking to prevent manual seeking
  const handleSeeking = useCallback(() => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      
      // If user tries to seek forward beyond last valid time, prevent it
      if (currentTime > lastValidTime) {
        videoRef.current.currentTime = lastValidTime;
      }
    }
  }, [lastValidTime]);

  // Handle video end
  const handleEnded = useCallback(() => {
    setIsFinished(true);
    setIsPlaying(false);
    onFinish();
  }, [onFinish]);

  // Handle play/pause
  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  // Handle start video button
  const handleStartVideo = useCallback(() => {
    if (videoRef.current) {
      setHasStarted(true);
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, []);

  // Handle play state change
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Initialize volume when video loads
  const initializeVolume = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.volume = initialVolume;
      setCurrentVolume(initialVolume);
      setIsMuted(initialVolume === 0);
    }
  }, [initialVolume]);

  // Handle volume change
  const handleVolumeChange = useCallback((newVolume: number) => {
    // Clamp volume within the specified range
    const clampedVolume = Math.max(minVolume, Math.min(maxVolume, newVolume));
    
    if (videoRef.current) {
      videoRef.current.volume = clampedVolume;
      setCurrentVolume(clampedVolume);
      setIsMuted(clampedVolume === 0);
    }
  }, [minVolume, maxVolume]);

  // Handle volume slider change
  const handleVolumeSliderChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(event.target.value);
    handleVolumeChange(newVolume);
  }, [handleVolumeChange]);

  // Handle mute toggle
  const handleMuteToggle = useCallback(() => {
    if (videoRef.current) {
      if (isMuted) {
        // Unmute: restore previous volume
        const volumeToRestore = previousVolume > 0 ? previousVolume : initialVolume;
        handleVolumeChange(volumeToRestore);
      } else {
        // Mute: save current volume and set to 0
        setPreviousVolume(currentVolume);
        handleVolumeChange(0);
      }
    }
  }, [isMuted, previousVolume, currentVolume, initialVolume, handleVolumeChange]);

  // Handle loaded metadata
  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setLastValidTime(0);
      setCurrentTime(0);
      setIsLoaded(true);
      initializeVolume();
    }
  }, [initializeVolume]);

  // Handle skip video for development
  const handleSkipVideo = useCallback(() => {
    setIsFinished(true);
    setIsPlaying(false);
    onFinish();
  }, [onFinish]);

  // Reset video when URL changes
  useEffect(() => {
    if (videoRef.current) {
      setLastValidTime(0);
      setCurrentTime(0);
      setIsFinished(false);
      setIsPlaying(false);
      setHasStarted(false);
      setIsLoaded(false);
      setCurrentVolume(initialVolume);
      setIsMuted(false);
      setPreviousVolume(initialVolume);
    }
  }, [url, initialVolume]);

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    maxWidth: '100%'
  };

  const videoStyle: React.CSSProperties = {
    display: 'block',
    maxWidth: '100%',
    width,
    height
  };

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    textAlign: 'center',
    padding: '20px',
    zIndex: 10
  };

  const startButtonStyle: React.CSSProperties = {
    background: '#007bff',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '20px',
    transition: 'background-color 0.2s'
  };

  const controlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px',
    background: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    borderRadius: '4px',
    marginTop: '10px'
  };

  const playPauseBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '20px',
    cursor: isFinished ? 'not-allowed' : 'pointer',
    padding: '5px',
    borderRadius: '4px',
    transition: 'background-color 0.2s',
    opacity: isFinished ? 0.5 : 1
  };

  const progressContainerStyle: React.CSSProperties = {
    flex: 1,
    height: '8px',
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '4px',
    overflow: 'hidden',
    position: 'relative'
  };

  const progressBarStyle: React.CSSProperties = {
    height: '100%',
    background: '#007bff',
    transition: 'width 0.1s ease',
    width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`
  };

  const timeDisplayStyle: React.CSSProperties = {
    fontSize: '14px',
    minWidth: '80px',
    textAlign: 'right'
  };

  const completedStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '20px',
    background: '#d4edda',
    color: '#155724',
    borderRadius: '4px',
    marginTop: '10px'
  };

  const skipButtonStyle: React.CSSProperties = {
    background: '#dc3545',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '10px',
    transition: 'background-color 0.2s'
  };

  const volumeControlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '10px'
  };

  const muteButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: 'white',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    transition: 'background-color 0.2s',
    minWidth: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const volumeSliderStyle: React.CSSProperties = {
    flex: 1,
    height: '6px',
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '3px',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none'
  };


  const volumeDisplayStyle: React.CSSProperties = {
    fontSize: '12px',
    minWidth: '40px',
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.8)'
  };

  return (
    <>
      <style>
        {`
          .video-reader-container input[type="range"]::-webkit-slider-thumb {
            appearance: none;
            -webkit-appearance: none;
            width: 16px;
            height: 16px;
            background: #007bff;
            border-radius: 50%;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          }
          
          .video-reader-container input[type="range"]::-moz-range-thumb {
            width: 16px;
            height: 16px;
            background: #007bff;
            border-radius: 50%;
            cursor: pointer;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
            border: none;
          }
        `}
      </style>
      <div className={`video-reader-container ${className}`} style={containerStyle}>
      <video
        ref={videoRef}
        src={url}
        style={videoStyle}
        onTimeUpdate={handleTimeUpdate}
        onSeeking={handleSeeking}
        onEnded={handleEnded}
        onPlay={handlePlay}
        onPause={handlePause}
        onLoadedMetadata={handleLoadedMetadata}
        controls={false} // Disable default controls to prevent seeking
        preload="metadata"
        poster="" // Remove poster to show first frame
      />
      
      {/* Start overlay - shown when video hasn't started yet */}
      {!hasStarted && !isLoaded && 
      <p>Chargement...</p>}
      {!hasStarted && isLoaded && (
        <div className="video-overlay" style={overlayStyle}>
          <div>
            <h3 style={{ marginBottom: '10px', fontSize: '20px' }}>
              {overlayText}
            </h3>
            <button 
              onClick={handleStartVideo}
              style={startButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#0056b3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#007bff';
              }}
            >
              {startButtonText}
            </button>
            
          </div>
        </div>
      )}
      
      {/* Custom controls - only shown when video has started */}
      {hasStarted && (
        <div className="video-controls" style={controlsStyle}>
          <button 
            onClick={handlePlayPause}
            className="play-pause-btn"
            disabled={isFinished}
            style={playPauseBtnStyle}
            onMouseEnter={(e) => {
              if (!isFinished) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            {isPlaying ? '⏸️' : '▶️'}
          </button>
          
          <div className="progress-container" style={progressContainerStyle}>
            <div 
              className="progress-bar"
              style={progressBarStyle}
            />
          </div>
          
          <span className="time-display" style={timeDisplayStyle}>
            {Math.floor(currentTime)}s / {Math.floor(duration)}s
          </span>
          
          {showSkipButton && !isFinished && (
            <button 
              onClick={handleSkipVideo}
              style={{
                ...skipButtonStyle,
                marginTop: 0,
                padding: '4px 8px',
                fontSize: '12px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#c82333';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#dc3545';
              }}
            >
              ⏭️ Skip
            </button>
          )}
        </div>
      )}

      {/* Volume Controls - shown when video has started and volume controls are enabled */}
      {hasStarted && showVolumeControls && (
        <div className="volume-controls" style={volumeControlsStyle}>
          <button 
            onClick={handleMuteToggle}
            style={muteButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : currentVolume > 0.5 ? '🔊' : currentVolume > 0 ? '🔉' : '🔈'}
          </button>
          
          <input
            type="range"
            min={minVolume}
            max={maxVolume}
            step="0.01"
            value={currentVolume}
            onChange={handleVolumeSliderChange}
            style={{
              ...volumeSliderStyle,
              background: `linear-gradient(to right, #007bff 0%, #007bff ${((currentVolume - minVolume) / (maxVolume - minVolume)) * 100}%, rgba(255, 255, 255, 0.3) ${((currentVolume - minVolume) / (maxVolume - minVolume)) * 100}%, rgba(255, 255, 255, 0.3) 100%)`
            }}
            title={`Volume: ${Math.round(currentVolume * 100)}%`}
          />
          
          <span style={volumeDisplayStyle}>
            {Math.round(currentVolume * 100)}%
          </span>
        </div>
      )}
      
      {isFinished && (
        <div className="video-completed" style={completedStyle}>
          <p>Vidéo terminée !</p>
        </div>
      )}
    </div>
    {!hasStarted && isLoaded && <p>Veillez à activer le son de votre navigateur pour profiter de la vidéo.</p>}

    </>
  );
};
